import * as React from 'react';
import { observer } from 'mobx-react';
import { useAuthStore, useStores } from 'store';
import { stageKey } from 'pages';
import { isNumber } from 'lodash';
import { useNavigate } from 'react-router-dom';
import { coerce, prerelease, valid as semverValid } from 'semver';
import {
    BasicCollapse,
    BasicForm,
    BasicTable,
    CheckboxField,
    CustomFormItem,
    IBasicTableColumn,
    IconTextButton,
    IRadioItem,
    ISimpleFileUploadProps,
    notification,
    RadioGroup,
    SelectFormItem,
    SimpleFileUploadItem,
    TextFieldItem,
    Typography,
    useForm,
    VerticalList,
} from 'ui-lib';
import {
    showDuplicatePackagesModal,
    showMissingPackageDependenciesModal,
    showPackageDetailsModal,
    showPackagesWithDescriptionModal,
} from 'pages/private/components/package-modal-components';
import { useSpinnerComponent } from 'pages/private/components/loading-component';

import { TDisplayPackageProps } from 'store/packages-store';
import { ISnapshotProps, METAPACKAGE_TYPE, METAPACKAGE_TYPES, SNAPSHOT_STATUS } from 'dto/software-snapshot-dto';
import { IPackageProps, PACKAGE_ARCH } from 'dto/packages-dto';

import { isNullOrEmpty } from 'common-utils';
import { confirmCancel } from '../utils';
import { VersionUtils } from 'common-utils/version-utils';
import { hasContainsIgnoreCase } from 'common-utils/string-utils';
import {
    PackageRowComponent,
    useUploadFilesCheckingStates,
} from 'pages/private/software/repositories-and-packages/package-utils';
import metaPackageCss from './metapackage.css';
import { showSavingErrorDialog } from 'pages/private/components/modal';
import { TableUtils } from 'pages/private/components/table-utils';
import { ResultEither } from 'hooks/use-loading.hook';

type IPackageRecord = TDisplayPackageProps & { inLatestSnapshot: boolean };

interface ISimpleFileUploadStates {
    fileUploadProps: ISimpleFileUploadProps;
    hasFileError: boolean;
    uploadPackages: File[];
    onClear: () => void;
}

export enum VersionType {
    NORMAL = 'normal',
    CUSTOM = 'custom',
}
const fullVersionFormat = (version: string, buildNumber: string): string => `${version}-${buildNumber}`;
const formatBuildNumber = (buildNumber: number): string => `${buildNumber}.${stageKey?.toLowerCase() ?? 'test'}`;
const toSemanticVersioning = (fullVersion: string): string => {
    return coerce(fullVersion)?.version;
};

const nextVersionBuildNumber = (versionWithBuildNumber: string, nextVersion: string): string => {
    if (!versionWithBuildNumber || toSemanticVersioning(versionWithBuildNumber) !== toSemanticVersioning(nextVersion)) {
        return formatBuildNumber(1000);
    }
    const currentBuild = prerelease(versionWithBuildNumber)?.[0];
    if (!isNumber(currentBuild)) {
        return formatBuildNumber(1000);
    }
    return formatBuildNumber((Number(currentBuild) || 0) + 1000);
};

const isBuildValid = (buildNumber: string): boolean => {
    const stage = stageKey?.toLowerCase() ?? 'test';
    const firstPartString = buildNumber.substring(0, buildNumber.indexOf('.'));
    const firstPartNumber = Number.parseInt(firstPartString);
    const lastPart = buildNumber.substring(buildNumber.indexOf('.') + 1);
    if (
        isNaN(firstPartNumber) ||
        firstPartString?.[0] === '0' ||
        firstPartString?.[0] === '-' ||
        firstPartString?.[0] === '+'
    ) {
        return false;
    }
    return lastPart === stage;
};

const useSimpleFileUploadStates = (): ISimpleFileUploadStates => {
    const [hasFileError, setHasFileError] = React.useState<boolean>(false);
    const [uploadPackages, setUploadPackages] = React.useState<File[]>([]);

    const { validateFiles } = useUploadFilesCheckingStates();

    const simpleFileUploadProps: ISimpleFileUploadProps = {
        fileList: uploadPackages,
        setFileList: setUploadPackages,
        allowedFileTypes: ['.deb'],
        limitFileSize: __LIMIT_PACKAGE_FILE_SIZE * 1024,
        beforeUploadChecking: (currentFiles: File[], newFiles: File[]): File[] => {
            const checkFileResult = validateFiles(currentFiles, newFiles);
            return checkFileResult.validFiles;
        },
        setFileError: setHasFileError,
        multiple: true,
    };
    return {
        fileUploadProps: simpleFileUploadProps,
        hasFileError: hasFileError,
        uploadPackages: uploadPackages,
        onClear: (): void => setUploadPackages([]),
    };
};

export const CreateMetaPackageComponent = observer(() => {
    const { configurationStore, packagesStore, snapshotStore } = useStores();
    const { assureApiAccount } = useAuthStore();
    const navigator = useNavigate();

    const toReleaseTable = (): void => {
        return navigator('/software/snapshots', {
            state: {
                snapshotType: SNAPSHOT_STATUS.RELEASED,
            },
        });
    };

    const [form] = useForm();
    const { SpinnerComponent, ...spinner } = useSpinnerComponent();

    const [metaPackageType, setMetaPackageType] = React.useState<METAPACKAGE_TYPE>(METAPACKAGE_TYPE.ALM);
    const [selectedPackages, setSelectedPackages] = React.useState<IPackageRecord[]>([]);
    const [basedOn, setBasedOn] = React.useState<ISnapshotProps>();

    const uploadComponentStates = useSimpleFileUploadStates();
    const metaPackageName = React.useMemo(() => `${metaPackageType}_SYS`, [metaPackageType]);

    const latestSnapshotOfType = React.useMemo<ISnapshotProps>(() => {
        return snapshotStore.getLatestDPUSnapshotByType(metaPackageType);
    }, [snapshotStore.displayDPUSnapshots, metaPackageType]);

    const latestSnapshotsOfType = React.useMemo<ISnapshotProps[]>(() => {
        return snapshotStore.displayDPUSnapshots[metaPackageType] || [];
    }, [snapshotStore.displayDPUSnapshots, metaPackageType]);

    const currentVersionAndBuildNumber = React.useMemo<string>(
        () => latestSnapshotOfType?.version,
        [latestSnapshotOfType],
    );

    const [versionType, setVersionType] = React.useState(VersionType.NORMAL);
    const [nextVersion, setNextVersion] = React.useState<string>();
    const [buildNumber, setBuildNumber] = React.useState<string>();
    const [clearAllForm, setClearAllForm] = React.useState<boolean>();

    React.useEffect(() => {
        setSelectedPackages([]);
        uploadComponentStates.onClear();
        form.resetFields();
    }, [clearAllForm]);

    React.useEffect(() => {
        setSelectedPackages([]);
    }, [metaPackageType]);

    React.useEffect(() => {
        let version = currentVersionAndBuildNumber;
        if (!currentVersionAndBuildNumber) {
            version = '1.0.0';
        }
        setNextVersion(toSemanticVersioning(version));
        const currentBuildNumber = nextVersionBuildNumber(currentVersionAndBuildNumber, nextVersion);
        setBuildNumber(currentBuildNumber);
        form.setFieldsValue({ buildNumber: currentBuildNumber });
    }, [currentVersionAndBuildNumber]);

    React.useEffect(() => {
        const currentBuildNumber = nextVersionBuildNumber(currentVersionAndBuildNumber, nextVersion);
        setBuildNumber(currentBuildNumber);
    }, [currentVersionAndBuildNumber, nextVersion]);

    const allPackages = React.useMemo<IPackageRecord[]>(
        () =>
            packagesStore.displayPackages
                .filter((item) => METAPACKAGE_TYPES.includes(item.type))
                .map((item) => ({
                    ...item,
                    inLatestSnapshot: latestSnapshotOfType?.packageIds?.includes(item.id) || false,
                })),
        [packagesStore.displayPackages, latestSnapshotOfType],
    );

    const customVersionExists = (version: string, build: string): boolean => {
        const fullCustomVersion = `${version}-${build}`;
        return snapshotStore.customVersionExists(fullCustomVersion, metaPackageType);
    };

    React.useEffect(() => {
        (async (): Promise<void> => {
            spinner.onStart();
            await Promise.all([
                packagesStore.loadEntities(assureApiAccount.accountId),
                snapshotStore.loadEntities(assureApiAccount.accountId),
            ]);
            spinner.onFinish();
        })();
    }, []);

    React.useEffect(() => {
        form.setFieldsValue({ currentVersion: currentVersionAndBuildNumber });
        form.setFieldsValue({ version: nextVersion });
        form.setFieldsValue({ buildNumber: buildNumber });
        form.setFieldsValue({ name: `${metaPackageName}-${fullVersionFormat(nextVersion, buildNumber)}` });
    }, [currentVersionAndBuildNumber, metaPackageName, nextVersion, buildNumber]);

    const PackageTable = React.useMemo(() => {
        const columns: IBasicTableColumn[] = [
            TableUtils.createBasicFilterColumn({
                title: 'Name',
                key: 'label',
                columnExtraOptions: {
                    width: '35%',
                    render: (text: string, record: IPackageRecord): React.ReactNode => (
                        <PackageRowComponent configurationStore={configurationStore} pkg={record} />
                    ),
                },
            }),
            TableUtils.createBasicEnumFilterColumn({
                title: 'Architecture',
                key: 'architecture',
                filters: Object.values(PACKAGE_ARCH).map((arch) => ({ text: arch, value: arch })),
            }),
            TableUtils.createBasicFilterColumn({
                title: 'Version',
                key: 'version',
                columnExtraOptions: {
                    sorter: (a: IPackageRecord, b: IPackageRecord) => VersionUtils.compare(a.version, b.version),
                },
            }),
            TableUtils.createBasicEnumFilterColumn({
                title: 'Type',
                key: 'type',
                defaultFilteredValue: [metaPackageType],
                filters: Object.keys(METAPACKAGE_TYPE).map((type) => ({ text: METAPACKAGE_TYPE[type], value: type })),
            }),
            TableUtils.createBasicFilterColumn({
                title: 'Repository',
                key: 'repositoryLabel',
            }),
            TableUtils.createBasicFilterTimeColumn({
                title: 'Updated date',
                key: 'updatedAt',
                compareKey: 'updatedAtTimestamp',
                defaultSortOrder: 'descend',
            }),
            {
                code: 'inLatestSnapshot',
                title: 'In latest release',
                width: '5%',
                dataIndex: 'inLatestSnapshot',
                filters: [
                    { text: 'TRUE', value: true },
                    { text: 'FALSE', value: false },
                ],
                onFilter: (filterValue: boolean, record: IPackageRecord) => record.inLatestSnapshot == filterValue,
                render: (value: boolean) => <CheckboxField label='' checked={value ?? false} />,
            },
        ];

        const Table = (): JSX.Element => (
            <BasicTable
                columns={columns}
                dataSource={allPackages}
                rowSelection={{
                    defaultSelectedRowKeys: selectedPackages.map((pack) => pack.id),
                    type: 'checkbox',
                    getCheckboxProps: (record: TDisplayPackageProps): { name: string } => {
                        return {
                            name: record['id'].toString(),
                        };
                    },
                    onChange: (selectedRowKeys, selectedRows): void => {
                        setSelectedPackages(selectedRows);
                    },
                }}
                rowKey='id'
            />
        );
        return <Table />;
    }, [allPackages, metaPackageType, latestSnapshotOfType, clearAllForm, basedOn]);

    const versionTypes: IRadioItem[] = [
        { value: VersionType.NORMAL, label: 'Normal versioning' },
        { value: VersionType.CUSTOM, label: 'Custom versioning' },
    ];

    const versionValidator = React.useMemo((): {
        validateVersion: (nextVersion: string, buildNumber: string) => ResultEither<void>;
        validateBuildNumber: (nextVersion: string, buildNumber: string) => ResultEither<void>;
    } => {
        switch (versionType) {
            case VersionType.NORMAL:
                return {
                    validateVersion: (nextVersion, buildNumber) => {
                        if (VersionUtils.lt(nextVersion, toSemanticVersioning(currentVersionAndBuildNumber))) {
                            return ResultEither.fail('The new version is lower than current version');
                        }
                        if (customVersionExists(nextVersion, buildNumber)) {
                            return ResultEither.fail('The full version already exists');
                        }
                        return ResultEither.ok(undefined);
                    },
                    validateBuildNumber: (nextVersion, buildNumber): ResultEither<void> => {
                        if (
                            VersionUtils.lte(
                                fullVersionFormat(nextVersion, buildNumber),
                                currentVersionAndBuildNumber,
                            )
                        ) {
                            return ResultEither.fail('The new version is lower than current version');
                        }
                        return ResultEither.ok(undefined);
                    },
                };
            case VersionType.CUSTOM:
                return {
                    validateVersion(nextVersion, buildNumber) {
                        if (customVersionExists(nextVersion, buildNumber)) {
                            return ResultEither.fail('The full version already exists');
                        }
                        return ResultEither.ok(undefined);
                    },
                    validateBuildNumber(nextVersion, buildNumber) {
                        if (customVersionExists(nextVersion, buildNumber)) {
                            return ResultEither.fail('The full version already exists');
                        }
                        return ResultEither.ok(undefined);
                    },
                };
        }
    }, [versionType, currentVersionAndBuildNumber]);

    React.useEffect(() => {
        form.validateFields(['version', 'buildNumber']);
    }, [versionType, currentVersionAndBuildNumber]);

    return (
        <SpinnerComponent
            child={
                <React.Fragment>
                    <BasicForm
                        form={form}
                        onSubmit={async (values): Promise<unknown> => {
                            const label = `${metaPackageName}-${nextVersion}-${buildNumber}`;
                            return showPackageDetailsModal(selectedPackages, {
                                title: (
                                    <React.Fragment>
                                        {label}
                                        <div className={metaPackageCss['preview-content']}>
                                            <BasicCollapse
                                                defaultActiveKey={
                                                    isNullOrEmpty(uploadComponentStates.uploadPackages)
                                                        ? []
                                                        : ['upload-package-files']
                                                }
                                                panels={[
                                                    {
                                                        key: 'upload-package-files',
                                                        header: 'Upload Package Files',
                                                        content: (
                                                            <VerticalList
                                                                title=''
                                                                className={metaPackageCss['vertical-list']}
                                                                size={'small'}
                                                                listItems={uploadComponentStates.uploadPackages.map(
                                                                    (item, index) => ({
                                                                        key: `${item.name}-${index}`,
                                                                        title: '',
                                                                        content: item.name,
                                                                    }),
                                                                )}
                                                            />
                                                        ),
                                                    },
                                                ]}
                                            />
                                        </div>
                                    </React.Fragment>
                                ),
                                type: 'confirm',
                                onOk: async () => {
                                    spinner.onStart();
                                    try {
                                        const result = await snapshotStore.createMetapackage(
                                            assureApiAccount.accountId,
                                            uploadComponentStates.uploadPackages,
                                            {
                                                label: label,
                                                description: values['description'],
                                                type: values['metapackageType'],
                                                version: nextVersion,
                                                build: buildNumber,
                                                packageIds: selectedPackages?.map((item) => item.id) || [],
                                            },
                                        );

                                        if (result.error) {
                                            if (result.error.type === 'DUPLICATED_PACKAGES_ERROR') {
                                                return showDuplicatePackagesModal({
                                                    packages: result.error.reason,
                                                });
                                            }
                                            return showSavingErrorDialog(result.error.reason);
                                        }

                                        notification.success({
                                            message: `The ${result.release.label} metadata package has been created`,
                                            placement: 'topRight',
                                        });

                                        const missingDependencies = packagesStore.detectDependencyPackages(
                                            result.packages.map((item) => item.id),
                                        );
                                        if (
                                            showMissingPackageDependenciesModal(
                                                { missingPackageDependencies: missingDependencies },
                                                toReleaseTable,
                                            )
                                        ) {
                                            return;
                                        }

                                        toReleaseTable();
                                    } catch (err) {
                                        showSavingErrorDialog(err.message);
                                    } finally {
                                        spinner.onFinish();
                                    }
                                },
                            });
                        }}
                        items={[
                            <TextFieldItem
                                key='metapackageName-text-field-item'
                                code='name'
                                label='Name'
                                initialValue={`${metaPackageName}-${nextVersion}`}
                                disabled={true}
                                labelAlign='right'
                                isRequired={true}
                            />,
                            <TextFieldItem
                                key='description-text-field-item'
                                code='description'
                                label='Description'
                                labelAlign='right'
                            />,
                            <SelectFormItem
                                key='metapackageType-select-form-item'
                                code='metapackageType'
                                label='Package Type'
                                isRequired={true}
                                labelAlign='right'
                                dataSource={METAPACKAGE_TYPES.map((item) => ({ value: item, label: item }))}
                                selectedValue={metaPackageType}
                                onChange={(value) => {
                                    setMetaPackageType(value);
                                    setBasedOn(undefined);
                                    form.setFieldsValue({ 'based-on': '' });
                                }}
                            />,
                            <CustomFormItem
                                key='versionType-custom-form-item'
                                code='versionType'
                                label=' '
                                labelAlign='right'
                                initialValue={versionType}
                            >
                                <RadioGroup
                                    items={versionTypes}
                                    value={versionType}
                                    onChange={(value: VersionType): void => setVersionType(value)}
                                />
                            </CustomFormItem>,
                            <TextFieldItem
                                key='currentVersion-text-field-item'
                                code='currentVersion'
                                label='Current version'
                                initialValue={currentVersionAndBuildNumber}
                                labelAlign='right'
                                disabled={true}
                            />,
                            <TextFieldItem
                                key='version-text-field-item'
                                code='version'
                                label='Version'
                                initialValue={nextVersion}
                                labelAlign='right'
                                isRequired={true}
                                validator={(rule, value, callback): void => {
                                    if (isNullOrEmpty(nextVersion)) return callback(undefined);
                                    if (!semverValid(nextVersion)) return callback('The version format is incorrect');
                                    if (isNullOrEmpty(currentVersionAndBuildNumber)) return callback(undefined);
                                    const result = versionValidator.validateVersion(nextVersion, buildNumber);
                                    if (result.isFail()) {
                                        return callback(result.errorMessage);
                                    }
                                    callback(undefined);
                                }}
                                onChange={(ev): void => {
                                    setNextVersion(ev.target.value);
                                }}
                            />,
                            <TextFieldItem
                                key='buildNumber-text-field-item'
                                code='buildNumber'
                                label='Build'
                                initialValue={buildNumber}
                                labelAlign='right'
                                disabled={false}
                                validator={(rule, value, callback): void => {
                                    if (isNullOrEmpty(buildNumber) || !isBuildValid(buildNumber)) {
                                        return callback('The build format is incorrect');
                                    }
                                    const result = versionValidator.validateBuildNumber(nextVersion, buildNumber);
                                    if (result.isFail()) {
                                        return callback(result.errorMessage);
                                    }
                                    callback(undefined);
                                }}
                                onChange={(ev): void => {
                                    setBuildNumber(ev.target.value);
                                }}
                            />,
                            <SelectFormItem
                                key='based-on-select-form-item'
                                code='based-on'
                                label='Based on'
                                labelAlign='right'
                                dataSource={latestSnapshotsOfType.map((item) => ({
                                    value: item.id,
                                    label: `${item.label} ${item.version}`,
                                    tooltip: item.description ? item.description : `${item.label} ${item.version}`,
                                    placement: 'topLeft',
                                }))}
                                selectedValue={basedOn?.id}
                                onChange={(snapshotId: number) => {
                                    const selectedSnapshot = latestSnapshotsOfType.find(
                                        (snapshot) => snapshot.id === snapshotId,
                                    );
                                    if (selectedSnapshot) {
                                        setSelectedPackages(
                                            allPackages.filter((pack) => selectedSnapshot.packageIds.includes(pack.id)),
                                        );
                                    }
                                    setBasedOn(selectedSnapshot);
                                }}
                                allowClear
                            />,
                            <SimpleFileUploadItem
                                key='fileUpload-item'
                                simpleFileUploadProps={uploadComponentStates.fileUploadProps}
                                code='fileUpload'
                                labelAlign='right'
                                label='Files'
                            />,
                            PackageTable,
                            <div key='rightBottomPanel-div' className={metaPackageCss['row']}>
                                <IconTextButton
                                    key='cancel'
                                    label='Cancel'
                                    onClick={(): void => confirmCancel(() => setClearAllForm(!clearAllForm))}
                                />
                                <IconTextButton
                                    key='save'
                                    label='Save'
                                    type='primary'
                                    disabled={
                                        uploadComponentStates.hasFileError ||
                                        (isNullOrEmpty(selectedPackages) &&
                                            isNullOrEmpty(uploadComponentStates.uploadPackages)) ||
                                        customVersionExists(nextVersion, buildNumber)
                                    }
                                    htmlType='submit'
                                />
                            </div>,
                        ]}
                    />
                </React.Fragment>
            }
        />
    );
});
